import {useEffect, useMemo, type RefObject} from 'react'

import type {DiffLine, FileDiffReference} from '../types'
import type {DiffAnnotation, Thread, ViewerData} from '../conversation-types'
import type {HunkData} from '../helpers/hunk-data-helpers'
import type {DiffAnchor} from '@github-ui/diffs/types'
import {SimpleAddCommentButton} from './SimpleAddCommentButton'
import {SimpleContextMenu} from './SimpleContextMenu'
import {useGetSelectedLineRange} from '../stores/selectors'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {DiffAddCommentButtonClickedEvent} from '../util/custom-events'
import {registerPointerExitObserver} from './grid-cells/shared-pointer-exit-tracker'

/**
 * The ActionBar is a set of buttons that appears on hover or focus of a cell in the diff grid.
 * The buttons will be absolutely positioned to the right side of the diff cell.
 */
type ActionBarProps = {
  annotations: DiffAnnotation[]
  authorLogin: string
  authorAvatarUrl: string
  copilotChatReference?: FileDiffReference
  currentHunk: HunkData | undefined
  diffAnchor: DiffAnchor
  diffLine: DiffLine
  hasDraftComment?: boolean
  isLeftSide: boolean
  isSplit: boolean
  nextHunk: HunkData | undefined
  previousHunk: HunkData | undefined
  shouldDisplayCollapseComments?: boolean
  shouldDisplayMinimizedComments?: boolean
  shouldDisplayComments?: boolean
  threads: Thread[]
  toggleViewingMarkers: () => void
  viewerData: ViewerData
  openStartConversationDialog: (shouldDoSuggestedChange: boolean) => void
  setIsContextMenuOpen: (value: boolean) => void
  setIsActionBarVisible: (value: boolean) => void
  contextMenuOpen: boolean
  filePath: string
  ref?: RefObject<HTMLTableCellElement | null>
}

export function SimpleActionBar({
  ref: _dialogReturnFocusRef,
  annotations,
  authorAvatarUrl,
  authorLogin,
  copilotChatReference,
  currentHunk,
  diffAnchor,
  diffLine,
  hasDraftComment,
  isLeftSide,
  isSplit,
  nextHunk,
  previousHunk,
  shouldDisplayCollapseComments,
  shouldDisplayMinimizedComments,
  shouldDisplayComments,
  threads,
  toggleViewingMarkers,
  viewerData,
  openStartConversationDialog,
  setIsContextMenuOpen,
  setIsActionBarVisible,
  filePath,
  contextMenuOpen,
}: ActionBarProps) {
  const getSelectedLineRange = useGetSelectedLineRange()
  const showStartConversation: boolean = useMemo(() => {
    if (!shouldDisplayComments) {
      return true
    }
    if (!viewerData.viewerCanComment) return false

    return true
  }, [shouldDisplayComments, viewerData.viewerCanComment])

  useEffect(() => {
    if (!_dialogReturnFocusRef?.current) return

    return registerPointerExitObserver({
      element: _dialogReturnFocusRef?.current,
      shouldIgnore: () => contextMenuOpen,
      onPointerExit: () => setIsActionBarVisible(false),
    })
  }, [_dialogReturnFocusRef, contextMenuOpen, setIsActionBarVisible])

  const onBeginComment = () => {
    let selectedLineRange = getSelectedLineRange()
    if (!selectedLineRange) {
      // if there is no selected line range, that means it was a single line comment, so pass that info in
      selectedLineRange = {
        startLineNumber: diffLine.blobLineNumber,
        endLineNumber: diffLine.blobLineNumber,
        startOrientation: isLeftSide ? 'left' : 'right',
        endOrientation: isLeftSide ? 'left' : 'right',
        diffAnchor,
      }
    }
    const addCommentEvent = new DiffAddCommentButtonClickedEvent(selectedLineRange, filePath)
    ssrSafeWindow?.dispatchEvent(addCommentEvent)
  }

  return (
    <>
      {showStartConversation && (
        <SimpleAddCommentButton
          hasDraftComment={hasDraftComment}
          lineSpacingPreference={viewerData.lineSpacingPreference}
          openStartConversationDialog={() => {
            openStartConversationDialog(false)
            onBeginComment()
          }}
          authorLogin={authorLogin}
          authorAvatarUrl={authorAvatarUrl}
        />
      )}
      <SimpleContextMenu
        showStartConversation={showStartConversation}
        viewerData={viewerData}
        openStartConversationDialog={() => {
          openStartConversationDialog(false)
          onBeginComment()
        }}
        setShouldStartNewConversationWithSuggestedChange={() => {
          openStartConversationDialog(true)
          onBeginComment()
        }}
        setIsContextMenuOpen={setIsContextMenuOpen}
        contextMenuOpen={contextMenuOpen}
        threads={threads}
        annotations={annotations}
        shouldDisplayMinimizedComments={shouldDisplayMinimizedComments ?? false}
        diffAnchor={diffAnchor}
        diffLine={diffLine}
        isLeftSide={isLeftSide}
        isSplit={isSplit}
        nextHunk={nextHunk}
        previousHunk={previousHunk}
        currentHunk={currentHunk}
        copilotChatReference={copilotChatReference}
        shouldDisplayCollapseComments={shouldDisplayCollapseComments}
        toggleViewingMarkers={toggleViewingMarkers}
      />
    </>
  )
}

SimpleActionBar.displayName = 'ActionBar'
